import { useState, useCallback } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { Picture } from '@/components/wpl/image/src/image';
import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { Button } from '@/libraries/design/components/button/src/button';
import { Lozenge } from '@/libraries/design/components/lozenge/src/lozenge';
import { wacToken } from '@/libraries/design/foundations';
import { Icon } from '@/libraries/wpl/foundations/src/icon';

import { useRoadmapFeaturesContext } from '../../roadmap-features/src/context/context.tsx';

// Constants
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FORM_CONFIG = {
	locale: 'US-CA',
	site: 'atlassian',
} as const;

// Helper function
const isValidEmail = (email: string): boolean => {
	return EMAIL_REGEX.test(email);
};

// Format english date with suffixes
const getOrdinalSuffix = (day: number, locale: string): string => {
	if (locale.startsWith('en')) {
		if (day > 3 && day < 21) return 'th';
		switch (day % 10) {
			case 1:
				return 'st';
			case 2:
				return 'nd';
			case 3:
				return 'rd';
			default:
				return 'th';
		}
	}
	return '';
};

const formatDateWithIntl = (isoDate: string, locale: string): string => {
	try {
		const date = new Date(isoDate);
		const intlLocale = locale || 'en-US';

		const month = new Intl.DateTimeFormat(intlLocale, {
			month: 'long',
			timeZone: 'UTC',
		}).format(date);
		const day = date.getUTCDate();
		const year = date.getUTCFullYear();
		const ordinal = getOrdinalSuffix(day, intlLocale);

		return `${month} ${day}${ordinal}, ${year}`;
	} catch {
		return isoDate;
	}
};

export interface RoadmapSubscriptionFormProps {
	lastUpdated: string;
	formId: string;
	formImage: {
		src: string;
		alt: string;
	};
	heading: string;
	description: string;
}

export const RoadmapSubscriptionForm = (props: RoadmapSubscriptionFormProps) => {
	const [email, setEmail] = useState('');
	const [honeypot, setHoneypot] = useState('');
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');
	const bxpClient = useBxpWebStargateClient();
	const intl = useIntl();
	const { locale } = useRoadmapFeaturesContext();

	const lastUpdatedDate = formatDateWithIntl(props.lastUpdated, locale);

	const handleSubmit = useCallback(
		async (e: React.FormEvent<HTMLFormElement>) => {
			e.preventDefault();

			// Honeypot check - silent return if bot
			if (honeypot) {
				return;
			}

			// Validate email
			if (!email || !isValidEmail(email)) {
				setError(
					intl.formatMessage({
						id: 'roadmapSubscription.error.invalidEmail',
						defaultMessage: 'Enter a valid email address.',
					}),
				);
				return;
			}

			setIsLoading(true);
			setError('');

			try {
				if (!bxpClient?.updateConsentPreferences) {
					throw new Error('Consent service unavailable');
				}

				await bxpClient.updateConsentPreferences({
					locale: FORM_CONFIG.locale,
					formId: props.formId,
					site: FORM_CONFIG.site,
					email,
					formUrl: window.location.href,
					consentLabelText: '',
					privacyPolicyText: '',
					consentCheckboxValue: true,
				});

				setIsSubmitted(true);
			} catch (err) {
				const errorMessage =
					err instanceof Error
						? err.message
						: intl.formatMessage({
								id: 'roadmapSubscription.error.serverError',
								defaultMessage: 'There was a server error submitting. Please try again.',
							});
				setError(errorMessage);
			} finally {
				setIsLoading(false);
			}
		},
		[email, honeypot, bxpClient],
	);

	return (
		<div id="roadmap-subscription-form" css={parentContainerStyle}>
			<div css={containerStyle}>
				<div css={rowStyle}>
					<div css={rightColumnStyle}>
						<div css={imageContainerStyle}>
							<Picture
								image={{
									src: props.formImage.src,
									alt: props.formImage.alt,
								}}
								style={{
									width: '100%',
									height: 'auto',
									maxHeight: '100%',
								}}
							/>
						</div>
					</div>

					<div css={leftColumnStyle}>
						<div css={lastUpdatedContainerStyle}>
							<Lozenge
								text={intl.formatMessage({
									id: 'roadmapSubscription.lastUpdated',
									defaultMessage: 'Last Updated',
								})}
								appearance="purple"
							/>
							<p css={dateTextStyle}>
								&nbsp;
								{lastUpdatedDate}
							</p>
						</div>

						<h1 css={headingStyle}>{props.heading}</h1>

						<p css={descriptionStyle}>{props.description}</p>

						{!isSubmitted ? (
							<form
								css={formStyle}
								onSubmit={handleSubmit}
								noValidate
								aria-label={intl.formatMessage({
									id: 'roadmapSubscription.form.ariaLabel',
									defaultMessage: 'Roadmap subscription form',
								})}
							>
								<label htmlFor="email-subscription" css={labelStyle}>
									{intl.formatMessage({
										id: 'roadmapSubscription.emailLabel',
										defaultMessage: 'Your company email',
									})}
								</label>
								<div css={emailInputContainerStyle}>
									<input
										type="email"
										name="email"
										id="email-subscription"
										value={email}
										onChange={(e) => setEmail(e.target.value)}
										required
										disabled={isLoading}
										aria-invalid={!!error}
										aria-describedby={error ? 'email-error' : undefined}
										css={emailInputStyle}
									/>
									<Button
										type="submit"
										variant="primary"
										size="large"
										isDisabled={isLoading}
										isLoading={isLoading}
										dataEventProps={{
											'data-product-key': 'roadmap',
											'data-event': 'clicked',
											'data-event-component': 'linkButton',
											'data-event-container': 'form',
											'data-label': 'subscribeButton',
										}}
										css={submitButtonStyle}
									>
										{intl.formatMessage({
											id: 'roadmapSubscription.button.subscribe',
											defaultMessage: 'Subscribe',
										})}
									</Button>
								</div>
								{/* Honeypot field */}
								<div css={honeypotStyle}>
									<input
										type="text"
										name="list_name"
										aria-label={intl.formatMessage({
											id: 'roadmapSubscription.honeypot.ariaLabel',
											defaultMessage: 'Keep this field blank',
										})}
										value={honeypot}
										onChange={(e) => setHoneypot(e.target.value)}
										tabIndex={-1}
									/>
								</div>
								{error && (
									<div css={errorStyle} id="email-error" role="alert">
										{error}
									</div>
								)}
							</form>
						) : (
							<div css={successMessageContainerStyle} role="status" aria-live="polite">
								<Icon name="checkmarkcircle" size="small" color={token('color.icon.success')} />

								<p css={successMessageTextStyle}>
									{intl.formatMessage({
										id: 'roadmapSubscription.success.message',
										defaultMessage: 'Thanks for subscribing!',
									})}
								</p>
							</div>
						)}
						<div css={successMessageContainerStyle} role="status" aria-live="polite" />
					</div>
				</div>
			</div>
		</div>
	);
};

const parentContainerStyle = css({
	marginBlockEnd: '20px',
	width: '100%',
});

const containerStyle = css({
	maxWidth: 1200,
	position: 'relative',
	marginInlineStart: 'auto',
	marginInlineEnd: 'auto',
});

const rowStyle = css({
	display: 'flex',
	flexWrap: 'wrap',
	gap: '40px',
	alignItems: 'flex-start',
});

const leftColumnStyle = css({
	flex: '1 1 100%',
	minWidth: 0,
	maxWidth: '600px',
	display: 'flex',
	flexDirection: 'column',
	gap: '20px',
	order: 2,
	'@media (min-width: 768px)': {
		order: 1,
		flex: '1 1 50%',
	},
});

const rightColumnStyle = css({
	flex: '1 1 100%',
	minWidth: 0,
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'stretch',
	order: 1,
	'@media (min-width: 768px)': {
		order: 2,
		flex: '1 1 40%',
		alignSelf: 'stretch',
	},
});

const lastUpdatedContainerStyle = css({
	display: 'flex',
	alignItems: 'center',
	gap: '8px',
	flexWrap: 'wrap',
});

const dateTextStyle = css({
	margin: 0,
	fontSize: '14px',
});

const headingStyle = css({
	fontSize: '48px',
	fontWeight: 700,
	margin: 0,
	lineHeight: 1.2,
});

const descriptionStyle = css({
	fontSize: '16px',
	lineHeight: 1.6,
	margin: 0,
});

const formStyle = css({
	display: 'flex',
	flexDirection: 'column',
	gap: '12px',
	maxWidth: '500px',
});

const labelStyle = css({
	fontSize: '14px',
	fontWeight: 600,
	display: 'block',
});

const emailInputContainerStyle = css({
	display: 'flex',
	gap: '12px',
	alignItems: 'stretch',
});

const emailInputStyle = css({
	width: '100%',
	padding: '10px 12px',
	fontSize: '14px',
	border: `1px solid ${wacToken('color.neutral.dark100')}`,
	borderRadius: '4px',
	outline: 'none',
	':focus': {
		borderColor: wacToken('color.primary.blue'),
		boxShadow: `0 0 0 2px ${wacToken('color.primary.blue')}33`,
	},
});

const honeypotStyle = css({
	position: 'absolute',
	insetInlineStart: '-9999px',
	width: '1px',
	height: '1px',
	overflow: 'hidden',
});

const submitButtonStyle = css({
	padding: '10px 24px',
	fontSize: '16px',
	fontWeight: 600,
	color: wacToken('color.neutral.light0'),
	backgroundColor: wacToken('color.primary.blue'),
	border: 'none',
	borderRadius: '4px',
	cursor: 'pointer',
	transition: 'background-color 0.2s',
	whiteSpace: 'nowrap',
	':hover:not(:disabled)': {
		backgroundColor: wacToken('color.primary.blue'),
	},
	':active:not(:disabled)': {
		transform: 'translateY(1px)',
	},
	':disabled': {
		opacity: 0.6,
		cursor: 'not-allowed',
	},
});

const errorStyle = css({
	color: wacToken('color.secondary.red'),
	fontSize: '14px',
	marginBlockStart: '8px',
});

const successMessageContainerStyle = css({
	display: 'flex',
	alignItems: 'center',
	gap: '12px',
	padding: '16px',
	borderRadius: '4px',
});

const successMessageTextStyle = css({
	margin: 0,
	fontSize: '16px',
});

const imageContainerStyle = css({
	marginBlockEnd: '-14px',
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'flex-end',
	height: '100%',
});
